/**
 * Demographics types for grant reporting and case management.
 * These types are aligned with US Census/federal reporting standards.
 */

// ============================================================================
// RACE / ETHNICITY
// ============================================================================

export type Race =
  | "american_indian_alaska_native"
  | "asian"
  | "black_african_american"
  | "hispanic_latino"
  | "native_hawaiian_pacific_islander"
  | "white"
  | "two_or_more"
  | "other"
  | "prefer_not_to_say"
  | "unknown";

export const RACE_LABELS: Record<Race, string> = {
  american_indian_alaska_native: "American Indian or Alaska Native",
  asian: "Asian",
  black_african_american: "Black or African American",
  hispanic_latino: "Hispanic or Latino",
  native_hawaiian_pacific_islander: "Native Hawaiian or Pacific Islander",
  white: "White",
  two_or_more: "Two or More Races",
  other: "Other",
  prefer_not_to_say: "Prefer Not to Say",
  unknown: "Unknown",
};

// ============================================================================
// SEX / GENDER
// ============================================================================

export type Sex = "male" | "female" | "other" | "prefer_not_to_say" | "unknown";

export const SEX_LABELS: Record<Sex, string> = {
  male: "Male",
  female: "Female",
  other: "Other",
  prefer_not_to_say: "Prefer Not to Say",
  unknown: "Unknown",
};

// ============================================================================
// LANGUAGE
// ============================================================================

export type PrimaryLanguage =
  | "english"
  | "spanish"
  | "chinese"
  | "vietnamese"
  | "tagalog"
  | "korean"
  | "arabic"
  | "russian"
  | "french"
  | "portuguese"
  | "asl"
  | "german"
  | "japanese"
  | "hindi"
  | "italian"
  | "polish"
  | "haitian_creole"
  | "other";

export const LANGUAGE_LABELS: Record<PrimaryLanguage, string> = {
  english: "English",
  spanish: "Spanish",
  chinese: "Chinese",
  vietnamese: "Vietnamese",
  tagalog: "Tagalog",
  korean: "Korean",
  arabic: "Arabic",
  russian: "Russian",
  french: "French",
  portuguese: "Portuguese",
  asl: "American Sign Language (ASL)",
  german: "German",
  japanese: "Japanese",
  hindi: "Hindi",
  italian: "Italian",
  polish: "Polish",
  haitian_creole: "Haitian Creole",
  other: "Other",
};

// ============================================================================
// REASON FOR SUPERVISION
// ============================================================================

export type ReasonForSupervision =
  | "domestic_violence"
  | "child_abuse"
  | "child_neglect"
  | "substance_abuse"
  | "mental_health"
  | "parental_conflict"
  | "reunification"
  | "court_ordered"
  | "safety_concerns"
  | "other";

export const REASON_LABELS: Record<ReasonForSupervision, string> = {
  domestic_violence: "Domestic Violence",
  child_abuse: "Child Abuse",
  child_neglect: "Child Neglect",
  substance_abuse: "Substance Abuse",
  mental_health: "Mental Health Concerns",
  parental_conflict: "Parental Conflict",
  reunification: "Reunification",
  court_ordered: "Court Ordered",
  safety_concerns: "Safety Concerns",
  other: "Other",
};

// ============================================================================
// SPECIAL NEEDS
// ============================================================================

export type SpecialNeed =
  | "autism_spectrum"
  | "adhd"
  | "learning_disability"
  | "physical_disability"
  | "hearing_impairment"
  | "visual_impairment"
  | "speech_language"
  | "developmental_delay"
  | "emotional_behavioral"
  | "medical_condition"
  | "other";

export const SPECIAL_NEEDS_LABELS: Record<SpecialNeed, string> = {
  autism_spectrum: "Autism Spectrum Disorder",
  adhd: "ADHD",
  learning_disability: "Learning Disability",
  physical_disability: "Physical Disability",
  hearing_impairment: "Hearing Impairment",
  visual_impairment: "Visual Impairment",
  speech_language: "Speech/Language Disorder",
  developmental_delay: "Developmental Delay",
  emotional_behavioral: "Emotional/Behavioral Disorder",
  medical_condition: "Chronic Medical Condition",
  other: "Other",
};

// ============================================================================
// AGE GROUPS
// ============================================================================

export type AgeGroup = "0-2" | "3-5" | "6-8" | "9-11" | "12-14" | "15-17" | "18+";

export const AGE_GROUP_LABELS: Record<AgeGroup, string> = {
  "0-2": "0-2 years",
  "3-5": "3-5 years",
  "6-8": "6-8 years",
  "9-11": "9-11 years",
  "12-14": "12-14 years",
  "15-17": "15-17 years",
  "18+": "18+ years",
};

// ============================================================================
// MARITAL STATUS
// ============================================================================

export type MaritalStatus =
  | "single"
  | "married"
  | "divorced"
  | "separated"
  | "widowed"
  | "partner";

export const MARITAL_STATUS_LABELS: Record<MaritalStatus, string> = {
  single: "Single",
  married: "Married",
  divorced: "Divorced",
  separated: "Separated",
  widowed: "Widower",
  partner: "Partner",
};

// ============================================================================
// EMPLOYMENT STATUS
// ============================================================================

export type EmploymentStatus =
  | "employed_full_time"
  | "employed_part_time"
  | "unemployed"
  | "self_employed"
  | "student"
  | "retired"
  | "disabled"
  | "homemaker"
  | "prefer_not_to_say";

export const EMPLOYMENT_STATUS_LABELS: Record<EmploymentStatus, string> = {
  employed_full_time: "Employed Full-Time",
  employed_part_time: "Employed Part-Time",
  unemployed: "Unemployed",
  self_employed: "Self-Employed",
  student: "Student",
  retired: "Retired",
  disabled: "Disabled",
  homemaker: "Homemaker",
  prefer_not_to_say: "Prefer Not to Say",
};

// ============================================================================
// INCOME LEVEL
// ============================================================================

export type IncomeLevel =
  | "under_15000"
  | "15000_29999"
  | "30000_49999"
  | "50000_74999"
  | "75000_99999"
  | "over_100000"
  | "prefer_not_to_say";

export const INCOME_LEVEL_LABELS: Record<IncomeLevel, string> = {
  under_15000: "Under $15,000",
  "15000_29999": "$15,000 - $29,999",
  "30000_49999": "$30,000 - $49,999",
  "50000_74999": "$50,000 - $74,999",
  "75000_99999": "$75,000 - $99,999",
  over_100000: "Over $100,000",
  prefer_not_to_say: "Prefer Not to Say",
};

// ============================================================================
// EDUCATION LEVEL
// ============================================================================

export type EducationLevel =
  | "hs_ged"
  | "some_college"
  | "two_year"
  | "four_year"
  | "masters"
  | "phd";

export const EDUCATION_LEVEL_LABELS: Record<EducationLevel, string> = {
  hs_ged: "High School / GED",
  some_college: "Some College",
  two_year: "2-Year Degree",
  four_year: "4-Year Degree",
  masters: "Master's Degree",
  phd: "PhD / Doctorate",
};

// ============================================================================
// HOUSING STATUS
// ============================================================================

export type HousingStatus =
  | "stable"
  | "temporary"
  | "homeless"
  | "transitional"
  | "unknown";

export const HOUSING_STATUS_LABELS: Record<HousingStatus, string> = {
  stable: "Stable",
  temporary: "Temporary",
  homeless: "Homeless",
  transitional: "Transitional",
  unknown: "Unknown",
};

// ============================================================================
// PRESENTING CONCERN
// ============================================================================

export type PresentingConcern =
  | "neglect"
  | "physical_abuse"
  | "emotional_abuse"
  | "domestic_violence_exposure"
  | "substance_use_parent"
  | "mental_health_concerns"
  | "parent_child_estrangement"
  | "behavioral_concerns_child"
  | "other";

export const PRESENTING_CONCERN_LABELS: Record<PresentingConcern, string> = {
  neglect: "Neglect",
  physical_abuse: "Physical Abuse",
  emotional_abuse: "Emotional Abuse",
  domestic_violence_exposure: "Domestic Violence Exposure",
  substance_use_parent: "Substance Use (Parent)",
  mental_health_concerns: "Mental Health Concerns",
  parent_child_estrangement: "Parent-Child Estrangement",
  behavioral_concerns_child: "Behavioral Concerns (Child)",
  other: "Other",
};

// ============================================================================
// VICTIMIZATION TYPE
// ============================================================================

export type VictimizationType =
  | "domestic_violence"
  | "sexual_assault"
  | "emotional"
  | "stalking"
  | "other"
  | "none"
  | "na";

export const VICTIMIZATION_TYPE_LABELS: Record<VictimizationType, string> = {
  domestic_violence: "Domestic Violence",
  sexual_assault: "Sexual Assault",
  emotional: "Emotional",
  stalking: "Stalking",
  other: "Other",
  none: "None",
  na: "N/A",
};

// ============================================================================
// SERVICE TYPE
// ============================================================================

export type ServiceType =
  | "supervised_visitation"      // Primary service type
  | "safe_exchange"              // Primary service type
  | "case_management"            // Case Management: Strengthening Families (Parenting)
  | "supervised_visit"           // Legacy - alias for supervised_visitation
  | "therapeutic_supervision"
  | "monitored_visitation"
  | "parent_education"
  | "reunification_therapy"
  | "family_counseling";

export const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  supervised_visitation: "Supervised Visitation",
  safe_exchange: "Safe Exchange",
  case_management: "Case Management: Strengthening Families (Parenting)",
  supervised_visit: "Supervised Visit", // Legacy label
  therapeutic_supervision: "Therapeutic Supervision",
  monitored_visitation: "Monitored Visitation",
  parent_education: "Parent Education",
  reunification_therapy: "Reunification Therapy",
  family_counseling: "Family Counseling",
};

/** Primary service types for dropdown selection */
export const PRIMARY_SERVICE_TYPES: ServiceType[] = [
  "supervised_visitation",
  "safe_exchange",
  "case_management",
];

// Legacy value mapping for backwards compatibility
export const SERVICE_TYPE_LEGACY_MAP: Record<string, ServiceType[]> = {
  both: ["supervised_visitation", "safe_exchange"],
  supervised_visit: ["supervised_visitation"], // Map old value to new
};

// ============================================================================
// SERVICE CLASSIFICATION
// ============================================================================

/**
 * Service classifications for Safe Exchange and Supervised Visitation.
 * These indicate the underlying context/reason for the service.
 */
export type ServiceClassification =
  | "domestic_violence"
  | "sexual_assault"
  | "dss_child_welfare";

export const SERVICE_CLASSIFICATION_LABELS: Record<ServiceClassification, string> = {
  domestic_violence: "Domestic Violence",
  sexual_assault: "Sexual Assault",
  dss_child_welfare: "DSS Child Welfare",
};

/** Service types that support classification */
export const CLASSIFIABLE_SERVICE_TYPES: ServiceType[] = [
  "supervised_visitation",
  "safe_exchange",
];

/**
 * Service type prefixes for generating case identifiers.
 * Format: {PREFIX}-{YEAR}-{SEQUENCE} (e.g., SV-2026-001)
 */
export const SERVICE_TYPE_PREFIXES: Record<ServiceType, string> = {
  supervised_visitation: "SV",
  safe_exchange: "EX",
  case_management: "CM",
  supervised_visit: "SV", // Legacy - same as supervised_visitation
  therapeutic_supervision: "TS",
  monitored_visitation: "MV",
  parent_education: "PE",
  reunification_therapy: "RT",
  family_counseling: "FC",
};

/** Default prefix when no service type is specified */
export const DEFAULT_CASE_PREFIX = "CS";

// ============================================================================
// REFERRAL SOURCE
// ============================================================================

/**
 * Source of referral for a case.
 */
export type ReferralSource =
  | "dv_agency"
  | "dss"
  | "court_ordered"
  | "djj"
  | "outside_source"
  | "self_referral"
  | "other";

export const REFERRAL_SOURCE_LABELS: Record<ReferralSource, string> = {
  dv_agency: "DV Agency",
  dss: "DSS",
  court_ordered: "Court-Ordered",
  djj: "DJJ",
  outside_source: "Outside Source",
  self_referral: "Self-Referral",
  other: "Other",
};

// ============================================================================
// EX PARTE STATUS
// ============================================================================

export type ExParteStatus = "yes" | "no" | "denied" | "not_requested" | "na";

export const EX_PARTE_STATUS_LABELS: Record<ExParteStatus, string> = {
  yes: "Yes",
  no: "No",
  denied: "Denied",
  not_requested: "Not Requested",
  na: "N/A",
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate age from date of birth.
 */
export const calculateAge = (dob: Date): number => {
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age--;
  }
  return age;
};

/**
 * Get age group from age.
 */
export const getAgeGroup = (age: number): AgeGroup => {
  if (age <= 2) return "0-2";
  if (age <= 5) return "3-5";
  if (age <= 8) return "6-8";
  if (age <= 11) return "9-11";
  if (age <= 14) return "12-14";
  if (age <= 17) return "15-17";
  return "18+";
};

/**
 * Get age group from date of birth.
 */
export const getAgeGroupFromDob = (dob: Date): AgeGroup => {
  return getAgeGroup(calculateAge(dob));
};
